import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApiError, api } from '../lib/api';
import { hhmm, shortDate, stopLabel } from '../lib/format';
import { useOrderActions } from '../lib/order-actions';
import { ListGroup, type ListOption } from '@allrouted/ui/ListControls';
import { Select } from '@allrouted/ui/Select';
import { KebabIcon, Menu, MenuChoice, MenuLabel } from '@allrouted/ui/Menu';
import { Resizer, usePaneSize } from '@allrouted/ui/Resizer';
import { IconButton, PlusIcon, RefreshIcon, TrashIcon } from '@allrouted/ui/Icons';
import { useUi } from '@allrouted/ui/Toaster';
import { OrdersMap, type OrderPin } from './orders/OrdersMap';
import { OrderDetail } from './orders/OrderDetail';
import { NewOrderDrawer } from './orders/NewOrderDrawer';
import { FitInDrawer } from './orders/FitInDrawer';
import { FindFitButton } from './UnassignedPanel';

/**
 * The day as work orders rather than as routes.
 *
 * The dispatch board answers "how is the fleet doing"; this answers "where is
 * this job and what did we promise". It is order-centric on purpose -- an order
 * that never made it onto a plan still belongs here, and is exactly the one
 * someone is looking for.
 *
 * Three panes across, as the incumbent has it: the day on the left, the order
 * in the middle, the map on the right. Reading an order and seeing where it is
 * are the same act, so neither one displaces the other.
 */

interface OrderRow extends Record<string, unknown> {
  id: string;
  name: string;
}

type SortKey = 'name' | 'eta' | 'sequence';
type GroupKey = 'none' | 'route' | 'status';
/**
 * What the list is narrowed to.
 *
 * Grouping by route already gathers the unrouted work under its own heading,
 * but a heading at the bottom of a list of three hundred is not the same thing
 * as a list of eleven -- and the question "what has nobody picked up yet" is
 * asked often enough to deserve its own control. Narrowing the list narrows
 * the map with it, because the pins are drawn from these rows.
 */
type FilterKey = 'all' | 'unassigned' | 'assigned';

const SORTS: ListOption<SortKey>[] = [
  { value: 'name', label: 'Name' },
  { value: 'eta', label: 'ETA' },
  { value: 'sequence', label: 'Route order' },
];

const FILTERS: ListOption<FilterKey>[] = [
  { value: 'all', label: 'All orders' },
  { value: 'unassigned', label: 'Unassigned only' },
  { value: 'assigned', label: 'On a route only' },
];

const GROUPS: ListOption<GroupKey>[] = [
  { value: 'none', label: 'No grouping' },
  { value: 'route', label: 'Route' },
  { value: 'status', label: 'Status' },
];

/** The time a row is sorted and displayed by: actual, else projected, else planned. */
function etaOf(row: OrderRow): number | null {
  return (
    (row.actual_arrival as number | null) ??
    (row.projected_arrival as number | null) ??
    (row.planned_arrival as number | null)
  );
}

function addressLine(address: unknown): string {
  if (!address || typeof address !== 'object') return '';
  const a = address as Record<string, string | null>;
  return [a.line1, a.city, a.region, a.postcode]
    .filter((part) => part && String(part).trim())
    .join(', ');
}

/**
 * How an order appears in a URL.
 *
 * The reference the operator's own systems use, falling back to our id only
 * when an order has none. A link someone can recognise is worth more than one
 * that is guaranteed unique in the abstract.
 */
function refOf(row: OrderRow): string {
  return (row.external_id as string) || row.id;
}

/** What the row is doing, from the order and its stop together. */
function rowStatus(row: OrderRow): string {
  const stop = row.status as string;
  if (stop === 'cancelled') return 'cancelled';
  if (!row.route_stop_id) return 'unassigned';
  return stop;
}

export function OrdersView({
  territoryId,
  date,
  selectedRef,
  onSelect,
}: {
  territoryId: string | null;
  date: string;
  /**
   * The open order as it appears in the URL: the operator's own reference where
   * there is one, so a link reads /orders/WO-6211 rather than a UUID.
   */
  selectedRef: string | null;
  /**
   * `auto` marks a selection the screen made rather than the user: falling back
   * to the first order should rewrite the history entry, not add one.
   */
  onSelect: (orderRef: string | null, auto?: boolean) => void;
}) {
  const ui = useUi();
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [clearing, setClearing] = useState(false);
  const [fitOrder, setFitOrder] = useState<{ id: string; name: string } | null>(null);
  const [vehicles, setVehicles] = useState<{ id: string; name: string }[]>([]);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<SortKey>('name');
  const [ascending, setAscending] = useState(true);
  const [group, setGroup] = useState<GroupKey>('none');
  const [filter, setFilter] = useState<FilterKey>('all');
  /*
   * Matches on days other than the one on screen.
   *
   * The list itself is one day's work and is filtered in the browser, which is
   * instant and right for scanning today. But the search that matters most is
   * the one where the day is the unknown: a customer rings quoting a Visit ID
   * or a ServiceBridge GPS ID and has no idea when the job is booked. Filtering
   * the loaded day answers that with "nothing matches", which is wrong rather
   * than merely unhelpful. So the same words also go to the server without a
   * date, and anything they find elsewhere is listed under its own heading.
   */
  const [elsewhere, setElsewhere] = useState<OrderRow[]>([]);
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  /* Shared between the form and the map while a pin is being corrected. */
  const [adjusting, setAdjusting] = useState<{ lat: number; lng: number } | null>(null);
  const [pinned, setPinned] = useState<{ lat: number; lng: number } | null>(null);

  /* Stable, because the detail builds an effect from it. */
  const handleAdjusting = useCallback((at: { lat: number; lng: number } | null) => {
    setAdjusting(at);
    if (!at) setPinned(null);
  }, []);

  /* Both sidebars are draggable; the order in the middle takes what is left. */
  const list = usePaneSize('allrouted.orders.list', 288, { min: 200, max: 520 });
  const map = usePaneSize('allrouted.orders.map', 380, { min: 240, max: 720 });

  /*
   * The selection handler and the current selection are read through refs, not
   * depended on. Either in the dependency list rebuilds `load` on every render,
   * which re-runs the effect, which sets state, which renders again -- three
   * round trips for the list and six for the order, on one page load.
   */
  const selectHandler = useRef(onSelect);
  selectHandler.current = onSelect;
  const openRef = useRef(selectedRef);
  openRef.current = selectedRef;
  /*
   * Bumped when the user asks for fresh data, so the open order re-reads with
   * the list. Deliberately not bumped by the initial load: that would fetch the
   * order once on mount and again the moment the list arrived.
   */
  const [generation, setGeneration] = useState(0);

  const load = useCallback(async () => {
    if (!territoryId) return;
    setLoading(true);
    try {
      const [{ orders: list }, { vehicles: fleet }] = await Promise.all([
        api.orders(territoryId, { date }),
        api.vehicles(territoryId),
      ]);
      setOrders(list as OrderRow[]);
      setVehicles(fleet as { id: string; name: string }[]);
      /*
       * Fall back to the first order only when the URL does not already name
       * one that exists. Opening a link and being bounced to a different order
       * is worse than an empty pane.
       */
      const rows = list as OrderRow[];
      const open = openRef.current;
      if (!open || !rows.some((o) => refOf(o) === open)) {
        selectHandler.current(rows[0] ? refOf(rows[0]) : null, true);
      }
    } finally {
      setLoading(false);
    }
  }, [territoryId, date]);

  useEffect(() => {
    void load();
  }, [load]);

  /*
   * Debounced, because this fires per keystroke and reaches the database. 250ms
   * is below the point a search feels laggy and above the point where typing
   * "8370" sends four queries.
   *
   * `key:value` is sent as an exact custom-field match rather than as free
   * text: an integration or a dispatcher who types `Visit ID:10000991835` wants
   * that record, not everything containing those digits.
   */
  useEffect(() => {
    const needle = query.trim();
    if (!territoryId || needle.length < 3) {
      setElsewhere([]);
      return;
    }

    let cancelled = false;
    const timer = window.setTimeout(() => {
      const colon = needle.indexOf(':');
      const params =
        colon > 0 ? { custom: needle, limit: 50 } : { q: needle, limit: 50 };
      setSearching(true);
      api
        .orders(territoryId, params)
        .then(({ orders: found }) => {
          if (!cancelled) setElsewhere(found as OrderRow[]);
        })
        /* A failed background search must not blank the list in front of you. */
        .catch(() => {
          if (!cancelled) setElsewhere([]);
        })
        .finally(() => {
          if (!cancelled) setSearching(false);
        });
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [territoryId, query]);

  const rows = useMemo(() => {
    /*
     * Narrowed before searching rather than after. Either order gives the same
     * rows, but this way the search runs over the smaller set and, more to the
     * point, the count beside the heading counts what is on screen.
     */
    const scope =
      filter === 'all'
        ? orders
        : orders.filter((o) =>
            filter === 'unassigned'
              ? rowStatus(o) === 'unassigned'
              : rowStatus(o) !== 'unassigned',
          );
    const needle = query.trim().toLowerCase();
    const matched = needle
      ? scope.filter((o) =>
          [
            o.name,
            o.external_id,
            o.customer_name,
            o.vehicle_name,
            addressLine(o.address),
            /*
             * Custom fields are searchable here too, keys and values alike: a
             * customer quotes a Visit ID or half a phone number and it is the
             * only handle they have on the job.
             */
            ...Object.entries((o.custom_fields ?? {}) as Record<string, unknown>).flat(),
          ]
            .filter(Boolean)
            .some((field) => String(field).toLowerCase().includes(needle)),
        )
      : scope;

    const direction = ascending ? 1 : -1;
    return [...matched].sort((a, b) => {
      switch (sort) {
        case 'eta': {
          /*
           * An unrouted order has no ETA. It sorts last whichever way the
           * arrow points, because "no time yet" is not "early" -- and it is
           * the row most likely to need attention, so it should not hide at
           * the top of an ascending list.
           */
          const [x, y] = [etaOf(a), etaOf(b)];
          if (x === null || y === null) return x === y ? 0 : x === null ? 1 : -1;
          return (x - y) * direction;
        }
        case 'sequence': {
          /* Route order only means something within a route; group by route. */
          const vehicle = String(a.vehicle_name ?? '').localeCompare(
            String(b.vehicle_name ?? ''),
          );
          if (vehicle !== 0) return vehicle * direction;
          return (Number(a.sequence ?? 0) - Number(b.sequence ?? 0)) * direction;
        }
        default:
          return String(a.name).localeCompare(String(b.name)) * direction;
      }
    });
  }, [orders, query, sort, ascending, filter]);

  /** The list as sections. One unnamed section when nothing is grouped. */
  const sections = useMemo(() => {
    if (group === 'none') return [{ label: '', rows }];

    const buckets = new Map<string, OrderRow[]>();
    for (const row of rows) {
      const key =
        group === 'route'
          ? ((row.vehicle_name as string) ?? 'Unassigned')
          : rowStatus(row);
      const bucket = buckets.get(key);
      if (bucket) bucket.push(row);
      else buckets.set(key, [row]);
    }
    return [...buckets.entries()].map(([label, rows]) => ({ label, rows }));
  }, [rows, group]);

  /* Only what is genuinely not already in the list above it. */
  const otherDays = useMemo(() => {
    if (elsewhere.length === 0) return [];
    const here = new Set(orders.map((o) => o.id));
    return elsewhere.filter((o) => !here.has(o.id));
  }, [elsewhere, orders]);

  const selected = useMemo(
    () =>
      orders.find((o) => refOf(o) === selectedRef) ??
      otherDays.find((o) => refOf(o) === selectedRef) ??
      null,
    [orders, otherDays, selectedRef],
  );

  const pins: OrderPin[] = useMemo(
    () =>
      rows.map((o) => ({
        id: o.id,
        name: String(o.name),
        lat: o.lat === null ? null : Number(o.lat),
        lng: o.lng === null ? null : Number(o.lng),
        status: rowStatus(o),
        technician: (o.vehicle_name as string) ?? null,
        /* What the bubble shows when the pin is clicked. */
        reference: (o.external_id as string) ?? null,
        customer: (o.customer_name as string) ?? null,
        address: addressLine(o.address),
        eta: rowStatus(o) === 'unassigned' ? '' : hhmm(etaOf(o)),
        /*
         * The label the plan gave the stop -- `10.1` -- not its position in
         * this list. An order off a route has none, and shows none.
         */
        label:
          o.route_stop_id === null || o.route_stop_id === undefined
            ? null
            : stopLabel(
                { label: o.stop_label as string | null, sequence: Number(o.sequence ?? 0) },
                o.vehicle_idx === null || o.vehicle_idx === undefined
                  ? null
                  : Number(o.vehicle_idx),
              ),
      })),
    [rows],
  );

  /*
   * The map's bubble offers the same two actions the order drawer does, from
   * the same place. A delete takes the open order with it, so the selection is
   * dropped and the list falls back on its own; an unassign only changes a
   * status, so the selection stays.
   */
  const orderActions = useOrderActions(territoryId);
  const unassignFromMap = useCallback(
    async (orderId: string, orderName: string) => {
      const row = orders.find((o) => o.id === orderId);
      const on = row?.scheduled_date ? String(row.scheduled_date).slice(0, 10) : date;
      if (await orderActions.unassign(orderId, orderName, on)) await load();
    },
    [orderActions, orders, date, load],
  );
  const deleteFromMap = useCallback(
    async (orderId: string, orderName: string) => {
      if (!(await orderActions.remove(orderId, orderName))) return;
      /*
       * The URL names the open order by reference, not by id, so the deleted
       * row has to be translated before the two can be compared -- and read
       * through the ref rather than the render, so this callback does not
       * rebuild on every selection.
       */
      const row = orders.find((o) => o.id === orderId);
      if (row && refOf(row) === openRef.current) onSelect(null);
      await load();
    },
    [orderActions, load, onSelect, orders],
  );

  /* The map works in ids; the URL works in references. Translate at the seam. */
  const selectFromMap = useCallback(
    (id: string) => {
      const row = orders.find((o) => o.id === id);
      if (row) onSelect(refOf(row));
    },
    [orders, onSelect],
  );

  /**
   * Empty the day being looked at.
   *
   * Asks the server what would go before asking the operator, so the question
   * carries a number rather than a promise. A day with work already started
   * needs a second, differently worded yes -- the first one is about tidying
   * up, the second is about discarding what a technician recorded.
   */
  const clearDay = async () => {
    if (!territoryId) return;
    setClearing(true);
    try {
      const preview = await api.clearDay(territoryId, date, { dryRun: true });
      if ((preview.wouldDelete ?? 0) === 0) {
        ui.notify('info', 'Nothing to clear', `No orders are scheduled on ${date}.`);
        return;
      }

      const ok = await ui.confirm({
        title: `Delete ${preview.wouldDelete} orders on ${date}?`,
        body: 'Any plan built for this day is emptied with them. Locked routes are left alone. This cannot be undone.',
        confirmLabel: 'Delete them',
      });
      if (!ok) return;

      let force = false;
      if (preview.started > 0) {
        force = await ui.confirm({
          title: `${preview.started} have already been started`,
          body: 'Deleting them discards the arrivals, completions and proof of delivery the technicians recorded. Everything else on the day goes either way.',
          confirmLabel: 'Delete those too',
        });
        if (!force) return;
      }

      const result = await api.clearDay(territoryId, date, { force });
      ui.notify(
        'ok',
        'Day cleared',
        `${result.deleted} orders deleted${result.plansCleared ? ' and the plan emptied' : ''}.`,
      );
      setGeneration((g) => g + 1);
      await load();
    } catch (err) {
      ui.notify('error', 'Could not clear the day', err instanceof ApiError ? err.message : String(err));
    } finally {
      setClearing(false);
    }
  };

  if (!territoryId) return <div className="empty">Select a territory.</div>;

  return (
    <div
      className="orders-layout"
      style={{
        gridTemplateColumns: `${list.size}px auto minmax(320px, 1fr) auto ${map.size}px`,
      }}
    >
      <div className="master">
        <div className="side-head">
          <h2>Orders</h2>
          <span className="count-pill">{rows.length}</span>
          <IconButton small label="New order" onClick={() => setNewOpen(true)}>
            <PlusIcon />
          </IconButton>
          <IconButton
            small
            label="Refresh orders"
            disabled={loading}
            onClick={() => {
              setGeneration((g) => g + 1);
              void load();
            }}
          >
            {loading ? <span className="spinner" /> : <RefreshIcon />}
          </IconButton>
          {/* Last, and the only destructive control here, so it sits furthest
            * from the one people press most. */}
          <IconButton
            small
            tone="danger"
            label={`Delete every order on ${date}`}
            disabled={clearing || rows.length === 0}
            onClick={() => void clearDay()}
          >
            {clearing ? <span className="spinner" /> : <TrashIcon />}
          </IconButton>
        </div>
        <div className="search" style={{ margin: '0 10px 8px' }}>
          <input
            type="text"
            value={query}
            placeholder="Search any day: order, customer, address, custom field"
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search orders"
          />
        </div>

        {/*
          * The filter stays out here; everything else is behind the menu.
          *
          * Four controls in a 288px sidebar overflowed, and the one that has to
          * stay visible is the filter -- it changes *what* is in the list, and
          * a reader who cannot see that they are on a subset will misread the
          * whole screen. Sort and grouping only rearrange what is already
          * there, so they can be a click away, and the menu says what they are
          * currently set to without being opened.
          */}
        <div className="list-controls">
          <Select
            value={filter}
            options={FILTERS}
            onChange={(v) => setFilter(v as FilterKey)}
          />
          <div style={{ flex: 1 }} />
          <span className="list-arrange">
            {SORTS.find((o) => o.value === sort)?.label}
            {ascending ? ' ↑' : ' ↓'}
            {group === 'none' ? '' : ` · ${GROUPS.find((o) => o.value === group)?.label}`}
          </span>
          <Menu label="Sort and group orders" icon={<KebabIcon />} align="right">
            <MenuLabel>Sort by</MenuLabel>
            {SORTS.map((option) => (
              <MenuChoice
                key={option.value}
                label={option.label}
                checked={sort === option.value}
                onSelect={() => setSort(option.value)}
              />
            ))}
            <MenuLabel>Direction</MenuLabel>
            <MenuChoice
              label="Ascending"
              checked={ascending}
              onSelect={() => setAscending(true)}
            />
            <MenuChoice
              label="Descending"
              checked={!ascending}
              onSelect={() => setAscending(false)}
            />
            <MenuLabel>Group by</MenuLabel>
            {GROUPS.map((option) => (
              <MenuChoice
                key={option.value}
                label={option.label}
                checked={group === option.value}
                onSelect={() => setGroup(option.value)}
              />
            ))}
          </Menu>
        </div>
        <div className="side-list">
          {rows.length === 0 && otherDays.length === 0 ? (
            <div className="empty">
              {searching
                ? 'Searching every day…'
                : query.trim()
                  ? 'Nothing matches that search, on this day or any other.'
                  : filter === 'unassigned'
                    ? 'Every order on this day is on a route.'
                    : filter === 'assigned'
                      ? 'Nothing on this day is on a route yet.'
                      : 'No orders on this day.'}
            </div>
          ) : (
            sections.map((section) => (
              <div key={section.label || 'all'}>
                {section.label ? (
                  <ListGroup label={section.label} count={section.rows.length} />
                ) : null}
                {section.rows.map((o) => {
                  const status = rowStatus(o);
                  return (
                    <div key={o.id} className="row-with-action">
                    <button
                      type="button"
                      className="order-row"
                      data-selected={o.id === selected?.id ? 'true' : undefined}
                      onClick={() => onSelect(refOf(o))}
                    >
                      <b>{String(o.name)}</b>
                      <span
                        className="delta"
                        data-tone={
                          status === 'completed'
                            ? 'ok'
                            : status === 'failed' || status === 'unassigned'
                              ? 'warn'
                              : 'idle'
                        }
                      >
                        {status === 'unassigned' ? 'unassigned' : hhmm(etaOf(o))}
                      </span>
                      <small>
                        {[o.vehicle_name, addressLine(o.address)].filter(Boolean).join(' · ') ||
                          '-'}
                      </small>
                    </button>
                    {status === 'unassigned' ? (
                      <FindFitButton onClick={() => setFitOrder({ id: o.id, name: String(o.name) })} />
                    ) : null}
                    </div>
                  );
                })}
              </div>
            ))
          )}

          {/*
            * Matches from other days, under their own heading and each carrying
            * its date. Kept separate rather than mixed into the list, because a
            * row without a date beside it reads as today's work -- which is how
            * somebody dispatches a job booked for next Tuesday.
            */}
          {otherDays.length > 0 ? (
            <>
              <ListGroup label="Other days" count={otherDays.length} />
              {otherDays.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  className="order-row"
                  data-selected={o.id === selected?.id ? 'true' : undefined}
                  onClick={() => onSelect(refOf(o))}
                >
                  <b>{String(o.name)}</b>
                  <span className="delta" data-tone="idle">
                    {o.scheduled_date ? shortDate(o.scheduled_date) : 'unscheduled'}
                  </span>
                  <small>
                    {[o.customer_name, addressLine(o.address)].filter(Boolean).join(' · ') || '-'}
                  </small>
                </button>
              ))}
            </>
          ) : null}
        </div>
      </div>

      <Resizer orientation="vertical" onResize={list.onResize} label="Resize the order list" />

      <div className="orders-detail">
        {selected ? (
          <OrderDetail
            key={selected.id}
            territoryId={territoryId}
            orderId={selected.id}
            generation={generation}
            vehicles={vehicles}
            onSaved={() => void load()}
            onAdjusting={handleAdjusting}
            pinned={pinned}
            /*
             * A deleted order has no row to return to, so the list reloads and
             * picks whatever is next; an unassigned one is still there and only
             * its status changed, so the same reload keeps it selected.
             */
            onRemoved={(what) => {
              /*
               * A deleted order has no row to go back to, so the URL stops
               * naming it and the list falls back on its own. An unassigned one
               * is still in the list with a new status, so the selection stays
               * and only the reload is needed.
               */
              if (what === 'deleted') onSelect(null);
              void load();
            }}
          />
        ) : (
          <div className="empty">Select an order.</div>
        )}
      </div>

      {/* Dragging right shrinks the map, so this handle counts backwards. */}
      <Resizer
        orientation="vertical"
        direction={-1}
        onResize={map.onResize}
        label="Resize the map"
      />

      <div className="orders-map">
        <OrdersMap
          orders={pins}
          /*
           * Narrowing the list reframes the map. Without it, filtering to the
           * four unassigned jobs left the viewport on the whole metro and the
           * four pins somewhere in it -- the filter had worked and looked like
           * it had not.
           */
          fitKey={filter}
          selectedId={selected?.id ?? null}
          onSelect={selectFromMap}
          adjusting={adjusting}
          onPinMove={(lat, lng) => setPinned({ lat, lng })}
          onUnassignOrder={(id, name) => void unassignFromMap(id, name)}
          onDeleteOrder={(id, name) => void deleteFromMap(id, name)}
        />
      </div>

      <FitInDrawer
        open={fitOrder !== null}
        territoryId={territoryId}
        date={date}
        today={date}
        routes={[]}
        order={fitOrder}
        onClose={() => setFitOrder(null)}
        /* This screen's map shows orders, not routes; the preview lives on Home. */
        onPreview={() => {}}
        onPlaced={() => void load()}
      />

      <NewOrderDrawer
        open={newOpen}
        territoryId={territoryId}
        date={date}
        onClose={() => setNewOpen(false)}
        onCreated={() => void load()}
      />
    </div>
  );
}
